import { useCallback, useState } from 'react'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  PayrollRunFileCategoryFragment,
  PayrollRunFileFragment,
  PayrollRunFilesDocument,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import buildUrl from 'modules/core/lib/buildUrl'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

interface FilesDataCollectionProps {
  companyId: number
  payrollRunId: number
  categories: Array<{ id: number } & PayrollRunFileCategoryFragment>
  emptyState: React.ComponentProps<typeof OneDataCollection>['emptyStates']
}

const FilesCategoryList = ({
  companyId,
  payrollRunId,
  categories,
  emptyState,
}: FilesDataCollectionProps) => {
  const [files, setFiles] = useState<PayrollRunFileFragment[]>([])

  const getCategoryCount = useCallback(
    (fileCategoryId: number) => {
      return files.filter((file) => file.fileCategoryId === fileCategoryId).length
    },
    [files]
  )

  const sourceFiles = useGraphqlDataCollectionSource({
    document: PayrollRunFilesDocument,
    mapData: (data) => {
      const nodes = data.compensations.filesConnection.nodes
      setFiles(nodes)
      return nodes
    },
    mapPagination: (total) => ({
      total: total.compensations.filesConnection.totalCount ?? 0,
    }),
    mapVariables: () => {
      return {
        companyId,
        payrollRunId,
        fileCategoryIds: categories.map((category) => category.id),
      }
    },
    defaultSortings: {
      field: 'createdAt',
      order: 'desc',
    },
    filters: {},
    presets: [],
    grouping: {
      collapsible: true,
      mandatory: true,
      hideSelector: true,
      defaultOpenGroups: true,
      groupBy: {
        fileCategoryId: {
          name: 'Category',
          label(fileCategoryId) {
            const categoryKey = categories.find(
              (category) => category.id === Number(fileCategoryId)
            )?.categoryType

            switch (categoryKey) {
              case 'payroll_result':
                return i18n.t(
                  'bookkeepers.clients.payroll_cycles.documents.file_categories.payroll_result'
                )
              case 'payslips':
                return i18n.t(
                  'bookkeepers.clients.payroll_cycles.documents.file_categories.payslips'
                )
              case 'payment_method':
                return i18n.t(
                  'bookkeepers.clients.payroll_cycles.documents.file_categories.payment_method'
                )
              case 'others':
                return i18n.t('bookkeepers.clients.payroll_cycles.documents.file_categories.others')
              case 'exported':
                return i18n.t(
                  'bookkeepers.clients.payroll_cycles.documents.file_categories.exported'
                )
              default:
                return ''
            }
          },
          itemCount: (fileCategoryId) => getCategoryCount(Number(fileCategoryId)),
        },
      },
    },
  })

  return (
    <OneDataCollection
      storage={false}
      source={sourceFiles}
      emptyStates={emptyState}
      visualizations={[
        {
          type: 'list',
          options: {
            itemDefinition: (item: PayrollRunFileFragment) => ({
              title: item.fileName,
              avatar: {
                type: 'file',
                file: {
                  name: item.fileName,
                  type: item.mimeType,
                },
              },
            }),
            fields: [
              {
                label: i18n.t('bookkeepers.clients.payroll_cycles.documents.list.created_at'),
                render: (item: PayrollRunFileFragment) => ({
                  type: 'text',
                  value: i18nDateToString(
                    stringToDate(item.createdAt, 'backend_datetime'),
                    'ui_short_with_time'
                  ),
                }),
              },
              {
                label: i18n.t('bookkeepers.clients.payroll_cycles.documents.list.created_by'),
                render: (item: PayrollRunFileFragment) => ({
                  type: 'person',
                  value: {
                    firstName: item.createdBy?.firstName ?? '',
                    lastName: item.createdBy?.lastName ?? '',
                    src: buildUrl(item.createdBy?.avatar ?? ''),
                  },
                }),
              },
            ],
          },
        },
      ]}
    />
  )
}

export default FilesCategoryList
